import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import PAGES from "@/seo/pages.json";
import { TOOLS } from "@/lib/tools";
import { useLang } from "@/i18n";
import { RelatedLinks } from "@/components/RelatedLinks";
import { Breadcrumb } from "@/components/Breadcrumb";

const GUIDE_KEYS = Object.keys(PAGES).filter((k) => PAGES[k].type === "guide");

export function GuidesIndex() {
  const { lang, locale } = useLang();
  const m = PAGES.guides[lang];
  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6" data-testid="guides-page">
      <p className="font-mono text-xs uppercase tracking-wider text-slate-400"><BookOpen className="mr-1 inline h-3.5 w-3.5" />PixelEdit Pro</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{m.h1}</h1>
      <p className="mt-4 max-w-2xl text-slate-600">{m.intro}</p>
      <div className="mt-12 grid gap-5">
        {GUIDE_KEYS.map((k) => {
          const g = PAGES[k];
          return (
            <Link key={k} to={g.paths[lang]} data-testid={`guide-card-${k}`} className="group rounded-2xl border border-slate-200 bg-white p-7 transition-colors hover:border-slate-900">
              <p className="font-mono text-[11px] text-slate-400">{new Date(g.date).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric", timeZone: "UTC" })}</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">{g[lang].h1}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{g[lang].description}</p>
              <ArrowRight className="mt-4 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-slate-900" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function GuidePage({ pageKey }) {
  const { t, lang, locale, toolHref } = useLang();
  const g = PAGES[pageKey];
  const m = g[lang];
  const tool = TOOLS[g.tool];
  return (
    <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="guide-page">
      <Breadcrumb pageKey={pageKey} />
      <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl">{m.h1}</h1>
      <p className="mt-3 font-mono text-xs text-slate-400"><time dateTime={g.date}>{new Date(g.date).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric", timeZone: "UTC" })}</time>{g.updated && g.updated !== g.date && <> · {t("guide.updated")} <time dateTime={g.updated}>{new Date(g.updated).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric", timeZone: "UTC" })}</time></>}</p>
      <p className="mt-6 text-lg leading-relaxed text-slate-700">{m.intro}</p>
      {m.sections.map((s) => (
        <section key={s.h} className="mt-10">
          <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">{s.h}</h2>
          <p className="mt-3 leading-relaxed text-slate-600">{s.p}</p>
        </section>
      ))}
      <div className="mt-14 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-slate-900 p-7 text-white">
        <p className="font-semibold">{t(`tools.${g.tool}.short`)}</p>
        <Button asChild className="rounded-full bg-white text-slate-900 hover:bg-slate-100" data-testid="guide-tool-cta">
          <Link to={toolHref(g.tool)}><tool.icon className="mr-2 h-4 w-4" />{t(`tools.${g.tool}.name`)}</Link>
        </Button>
      </div>
      <RelatedLinks tool={g.tool} current={pageKey} />
    </article>
  );
}
